import { useEffect, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import { useSelector } from "react-redux";
import { RootState } from "../../../../redux/store";
import { useQuery } from "@tanstack/react-query";

import { saveAs } from "file-saver";
import { AllCountriesAndStates } from "../../../../types";
import { fetchAllCountriesAndStates } from "../../../../services/apis";

export default function LogoAddressComponentDownload() {
  const [allCountriesAndStates, setAllCountriesAndStates] = useState<AllCountriesAndStates[]>([]);

  // Get the site data from the Redux store
  const siteData = useSelector((state: RootState) => state.siteDirectory.sitesData);

  const {
    data: allCountriesData,
    isLoading: isAllCountriesDataLoading,
    isSuccess: isAllCountriesDataSuccess,
  } = useQuery({
    queryKey: ["allCountries"],
    queryFn: fetchAllCountriesAndStates,
  });

  useEffect(() => {
    if (allCountriesData !== undefined && isAllCountriesDataSuccess && !isAllCountriesDataLoading) {
      const updatedData: AllCountriesAndStates[] = [];
      for (const i in allCountriesData?.data) {
        const itemSub = allCountriesData?.data[i];
        const { name, states, iso2 } = itemSub;
        const item: AllCountriesAndStates = {
          countryName: name,
          states: states,
          countryCode: iso2,
        };
        if (item.countryName === "United States" || item.countryName === "Canada") {
          updatedData.push(item);
        }
      }
      // console.log("updatedData ", updatedData);
      const sortedCountries = updatedData.sort((a, b) => a.countryName.localeCompare(b.countryName));
      console.log("sortedCountries ", sortedCountries);
      setAllCountriesAndStates(sortedCountries);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allCountriesData, isAllCountriesDataSuccess, isAllCountriesDataLoading]);

  const getStateCode = (input: string): string | null => {
    if (!input) return null;

    const normalizedInput = input.toLowerCase().trim();

    for (const country of allCountriesAndStates) {
      // check by full state name
      let foundState = country.states.find((s) => s.name.toLowerCase() === normalizedInput);

      // if not found, check by abbreviation
      if (!foundState) {
        foundState = country.states.find((s) => s.state_code.toLowerCase() === normalizedInput);
      }

      if (foundState) {
        return foundState.state_code;
      }
    }
    return null;
  };

  const handleDownload = () => {
    const { project_name } = siteData.project_data;
    const { site_name } = siteData.site_data;
    const date = new Date();
    const formattedDate = `${("0" + (date.getMonth() + 1)).slice(-2)}-${("0" + date.getDate()).slice(-2)}-${date.getFullYear().toString().slice(-2)}`;
    const fileName = `${project_name}_${site_name}_${formattedDate}.csv`.replace(/\s+/g, "_");

    const csvContent = `Total Units,Active Alerts,Energy Savings YTD (kwh),Carbon reduction savings (mtons),Electric demand last 15 min,Demand response\n${siteData.site_data.total_units},${
      siteData.active_alerts_data.active_alerts
    },${siteData.energy_savings_data.total_savings},${siteData.energy_savings_data.carbon_reduction_savings},${siteData.electric_demand_last_15_min},${
      siteData.dr_event_enrolled ? "In Progress" : "Not In Progress"
    }`;

    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;",
    });
    saveAs(blob, fileName);
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        flexDirection: {
          xs: "column", // Stack vertically on small screens
          sm: "row", // Side by side from sm breakpoint onwards
        },
        alignItems: {
          xs: "flex-start", // Align items to the start when stacked
          sm: "center", // Center items when side by side
        },
        gap: 2,
        marginBottom: {
          xs: 2,
        },
      }}
    >
      <Box>
        {/* {siteData?.project_data.logo_url && <img src={siteData?.project_data.logo_url} alt="PaceLogo" style={{ height: "45px", objectFit: "contain" }} />} */}
        <Typography id="modal-modal-title" variant="body1">
          {siteData ? (
            <>
              {siteData.site_data.address_line1} <br />
              {siteData.site_data.address_line2 ? (
                <>
                  {siteData.site_data.address_line2} <br />
                </>
              ) : null}
              {siteData.site_data.city ? <>{siteData.site_data.city},&nbsp;</> : null}
              {siteData.site_data.state ? <>{getStateCode(siteData.site_data.state)}&nbsp;</> : null}
              {siteData.site_data.postal_code ? (
                <>
                  {siteData.site_data.postal_code} <br />
                </>
              ) : null}
              {siteData.site_data.phone_number ? <>{siteData.site_data.phone_number}</> : null}
            </>
          ) : (
            <>
              625 S. The City Drive <br />
              Orange, CA 912345 <br />
              949-123-4567
            </>
          )}
        </Typography>
      </Box>
      <Box>
        <Button
          sx={{
            minWidth: {
              xs: "100%", // Full width on xs breakpoint
              sm: "auto", // Default width on sm and above
            },
          }}
          variant="outlined"
          color="primary"
          size="medium"
          endIcon={<DownloadIcon />}
          onClick={() => handleDownload()}
        >
          Download operations report
        </Button>
      </Box>
    </Box>
  );
}
